import { useState, useEffect, useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { makeLaneKey } from '@/lib/spotRateEngine';
import { fetchSpotRateCached } from '@/lib/spotRateTelemetry';
import { geocodeLocation, haversineDistance } from '@/lib/loadSearch';

export type TruckStatus = "active" | "expired" | "private" | "booked";

export interface MyTruckRow {
  id: string;
  ref: string;
  origin: string;
  destination: string;
  /** Raw DB equipment_type, used for spot-rate engine input */
  equipmentRaw: string;
  equipmentType: string;
  lengthFt: number;
  weightLbs: number;
  loadType: string;
  availableFrom: string;
  availableTo: string;
  ageHours: number;
  status: TruckStatus;
  ratePerMile: number | null;
  /** Live load-side market RPM for this lane+equipment (null when no comps) */
  marketRpm: number | null;
  /** Direction of the carrier's ask vs market RPM (null when no signal or no ask) */
  rateVsMarket: "up" | "down" | "neutral" | null;
  /** Percent diff (carrier ask − market) / market × 100 — null when no signal */
  rateVsMarketPercent: number | null;
  comments: string | null;
  contactInfo: string | null;
  createdAt: string;
}

const EQ_MAP: Record<string, string> = {
  van: 'Van', reefer: 'Reefer', flatbed: 'Flatbed',
  step_deck: 'Step Deck', power_only: 'Power Only',
  box_truck: 'Box Truck', hotshot: 'Hotshot',
  conestoga: 'Conestoga', rgn: 'RGN', tanker: 'Tanker',
  intermodal: 'Intermodal', car_hauler: 'Car Hauler',
  dump_trailer: 'Dump', lowboy: 'Lowboy', other: 'Other',
};

/** Only treat strict "City, ST" strings as geocodable. Skips "Northeast US", "Anywhere", etc. */
function isCityState(s: string | null | undefined): boolean {
  if (!s) return false;
  return /^[A-Za-z][A-Za-z .'-]+,\s*[A-Z]{2}\b/.test(s.trim());
}

export function useMyTrucks() {
  const { user } = useAuth();
  const [trucks, setTrucks] = useState<MyTruckRow[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user?.id;
  const qc = useQueryClient();

  const fetchTrucks = useCallback(async () => {
    if (!userId) { setLoading(false); return; }

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('truck_postings')
        .select('*')
        .eq('created_by', userId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;

      const now = Date.now();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Hide terminal-removed statuses entirely (parity with useMyLoads HIDDEN_STATUSES).
      const HIDDEN_STATUSES = new Set(['cancelled', 'deleted']);
      const visible = (data || []).filter(t => !HIDDEN_STATUSES.has(t.status as string));

      const rows: MyTruckRow[] = visible.map(t => {
        const ageMs = now - new Date(t.created_at).getTime();
        const ageHours = Math.floor(ageMs / (1000 * 60 * 60));

        const availTo = new Date(t.available_to);
        availTo.setHours(0, 0, 0, 0);
        const isExpired = availTo < today && t.status !== 'private' && t.status !== 'booked';

        let status: TruckStatus = 'active';
        if (t.status === 'booked') status = 'booked';
        else if (t.status === 'private') status = 'private';
        else if (isExpired) status = 'expired';

        return {
          id: t.id,
          ref: t.reference_id || '—',
          origin: t.origin_location || '—',
          destination: t.destination_location || '—',
          equipmentRaw: t.equipment_type || '',
          equipmentType: EQ_MAP[t.equipment_type] || t.equipment_type || '—',
          lengthFt: t.length_ft || 0,
          weightLbs: t.weight_capacity_lbs || 0,
          loadType: t.load_type || '—',
          availableFrom: t.available_from,
          availableTo: t.available_to,
          ageHours,
          status,
          ratePerMile: t.rate_per_mile != null ? Number(t.rate_per_mile) : null,
          marketRpm: null,
          rateVsMarket: null,
          rateVsMarketPercent: null,
          comments: t.comments || null,
          contactInfo: t.contact_info || null,
          createdAt: t.created_at,
        };
      });

      setTrucks(rows);

      // Fire-and-forget: enrich with live load-side market RPM
      enrichWithMarketRpm(qc, rows).then(updated => {
        if (updated) setTrucks(updated);
      });
    } catch (err) {
      console.error('useMyTrucks error:', err);
      setTrucks([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTrucks();
  }, [fetchTrucks]);

  return { trucks, loading, refetch: fetchTrucks };
}

/* ─── Market RPM enrichment ──────────────────────────────────────
 * For each truck with a real "City, ST → City, ST" lane, geocode both
 * endpoints, compute lane miles, and run the same spotRateEngine that
 * powers My Loads. Group by lane+equipment to dedupe queries.
 * No fabricated numbers — only fills marketRpm when comps exist.
 */
async function enrichWithMarketRpm(qc: QueryClient, rows: MyTruckRow[]): Promise<MyTruckRow[] | null> {
  const eligible = rows.filter(r =>
    r.equipmentRaw &&
    isCityState(r.origin) &&
    isCityState(r.destination)
  );
  if (eligible.length === 0) return null;

  // Group by lane+equipment to share one geocode + one spot query
  type Group = { rows: MyTruckRow[]; origin: string; dest: string; equipment: string; oCity: string; oState: string; dCity: string; dState: string };
  const groups = new Map<string, Group>();
  for (const t of eligible) {
    const [oCity, oState] = t.origin.split(',').map(s => s.trim());
    const [dCity, dState] = t.destination.split(',').map(s => s.trim());
    const oSt = oState.split(' ')[0];
    const dSt = dState.split(' ')[0];
    const key = makeLaneKey(oCity, oSt, dCity, dSt, t.equipmentRaw);
    const g = groups.get(key) || { rows: [], origin: t.origin, dest: t.destination, equipment: t.equipmentRaw, oCity, oState: oSt, dCity, dState: dSt };
    g.rows.push(t);
    groups.set(key, g);
  }

  const results = new Map<string, { marketRpm: number }>();
  const entries = Array.from(groups.entries());
  const BATCH = 6;

  for (let i = 0; i < entries.length; i += BATCH) {
    const batch = entries.slice(i, i + BATCH);
    await Promise.all(batch.map(async ([, group]) => {
      try {
        const [originGeo, destGeo] = await Promise.all([
          geocodeLocation(group.origin),
          geocodeLocation(group.dest),
        ]);
        if (!originGeo || !destGeo) return;
        const miles = Math.round(haversineDistance(originGeo.lat, originGeo.lon, destGeo.lat, destGeo.lon));
        if (miles <= 0) return;

        const spot = await fetchSpotRateCached(qc, {
          originLat: originGeo.lat,
          originLon: originGeo.lon,
          destLat: destGeo.lat,
          destLon: destGeo.lon,
          laneMiles: miles,
          equipmentType: group.equipment,
          originCity: group.oCity,
          originState: group.oState,
          destCity: group.dCity,
          destState: group.dState,
        }, { skipTrend: true });

        if (!spot || !spot.marketRpm) return;
        for (const r of group.rows) {
          results.set(r.id, { marketRpm: spot.marketRpm });
        }
      } catch {
        /* silent — leave marketRpm null */
      }
    }));
  }

  if (results.size === 0) return null;

  return rows.map(r => {
    const hit = results.get(r.id);
    if (!hit) return r;
    const marketRpm = Math.round(hit.marketRpm * 100) / 100;
    let rateVsMarket: "up" | "down" | "neutral" | null = null;
    let rateVsMarketPercent: number | null = null;
    if (r.ratePerMile != null && r.ratePerMile > 0 && marketRpm > 0) {
      const diff = ((r.ratePerMile - marketRpm) / marketRpm) * 100;
      rateVsMarketPercent = Math.round(diff * 10) / 10;
      rateVsMarket = diff > 2 ? "up" : diff < -2 ? "down" : "neutral";
    }
    return { ...r, marketRpm, rateVsMarket, rateVsMarketPercent };
  });
}
